import { FC, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, Card, CardContent, CardHeader, Grid, IconButton, Typography } from '@material-ui/core';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import EditIcon from '@material-ui/icons/Edit';
import { BasicProfile, ChannelPartner } from 'src/interfaces/new-channel-partner/channelPartner';
import RedeemIcon from '@material-ui/icons/Redeem';
import { s3FileApi } from 'src/api/s3file';
import { rewardApi } from 'src/api/rewardTransaction';
import viewContext from 'src/viewContext';
import { getAddress } from 'src/utils/address';
import { ChevronDown as ChevronDownIcon } from '../../../../../icons/chevron-down';
import { ChevronLeft as ChevronLeftIcon } from '../../../../../icons/chevron-left';
import { EditBasicInfo } from '../../dialogs/editBasicInfo';

interface ChannelPartnerDetailProps {
  cpDetailData: ChannelPartner;
  addProfilePhoto: (data: any) => void;
  updateBasicDetails: (data: BasicProfile) => Promise<boolean>;
}

export const BasicInfoCard: FC<ChannelPartnerDetailProps> = (props) => {
  const { cpDetailData, addProfilePhoto, updateBasicDetails } = props;
  const [displayBasicInfo, setDisplayBasicInfo] = useState<boolean>(true);
  const [openEditBasicInfo, setOpenEditBasicInfo] = useState<boolean>(false);
  const [rewardValue, setRewardValue] = useState<number>(0);
  const { mobileView } = useContext(viewContext);
  const [imageUrl, setImageUrl] = useState<string>('');

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    await addProfilePhoto(uploadedFile);
  };

  const setImageData = async () => {
    const imagePath = (cpDetailData.profileImage && !!cpDetailData.profileImage?.key) ? cpDetailData.profileImage?.key : '';
    if (imagePath !== '') {
      const url = await s3FileApi.getImageFile(imagePath);
      setImageUrl(url || '');
    }
  };

  const getRewardPoints = async () => {
    const result = await rewardApi.getUserRewardBalance(cpDetailData?.userName);
    setRewardValue(result);
  };

  useEffect(() => {
    if (cpDetailData?.id) {
      getRewardPoints();
    }
  }, [cpDetailData?.id]);

  useEffect(() => {
    setImageData();
  }, [cpDetailData?.profileImage]);

  const getGridItem = (label: string, value: string | number) => (
    <Grid
      container
      direction="row"
      ml={3}
    >
      <Grid
        item
        md={6}
        xs={6}
        sm={6}
      >
        <Typography
          variant="button"
        >
          {label}
        </Typography>
      </Grid>
      <Grid
        item
        md={6}
        xs={6}
        sm={6}
      >
        <Typography
          sx={{
            overflowWrap: 'break-word'
          }}
          color="#506176"
          variant="body2"
        >
          {value}
        </Typography>
      </Grid>

    </Grid>
  );

  return (
    <>
      <Card
        variant="outlined"
      >
        <CardHeader
          titleTypographyProps={{ variant: 'h2' }}
          title="Basic Information"
          action={(
            <>
              <IconButton
                sx={{ py: 0 }}
                aria-label="edit"
                onClick={() => setOpenEditBasicInfo(true)}
              >
                <EditIcon />
              </IconButton>
              { displayBasicInfo && (
                <IconButton
                  sx={{ py: 0 }}
                  aria-label="view"
                  onClick={() => {
                    setDisplayBasicInfo(false);
                  }}
                >
                  <ChevronDownIcon />
                </IconButton>
              )}
              { !displayBasicInfo && (
                <IconButton
                  aria-label="view"
                  onClick={() => {
                    setDisplayBasicInfo(true);
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
            </>
          )}
        />
        {displayBasicInfo && (
        <CardContent
          sx={{
            mt: -3
          }}
        >
          <Grid
            container
          >
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginX: 2
              }}
              item
              md={1}
              xs={12}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  position: 'relative',
                  maxWidth: '80%'
                }}
              >
                <Avatar
                  src={imageUrl}
                  sx={{
                    height: 100,
                    width: 100,
                    mt: 2
                  }}
                  variant="circular"
                />
                <label
                  htmlFor="imageUpload"
                >
                  <input
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    name="file"
                    id="imageUpload"
                    onChange={(e) => handleFileUpload(e)}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    aria-label="upload picture"
                    component="span"
                    sx={{ position: 'absolute', mt: 10, ml: -4, color: '#fff', background: '#004481', borderRadius: '50%' }}
                  >
                    <PhotoCameraIcon fontSize="small" />
                  </IconButton>
                </label>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  textAlign: mobileView ? 'left' : 'center',
                  paddingY: 1
                }}
              >
                {cpDetailData?.referralCode?.toUpperCase()}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: mobileView ? 'left' : 'center',
                  color: 'primary.main'
                }}
              >
                <RedeemIcon
                  titleAccess="Reward Points"
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: 'black',
                    textAlign: 'center',
                    pb: 1,
                    pl: 1,
                  }}
                >
                  {rewardValue}
                </Typography>
              </Box>
            </Grid>
            <Grid
              md={5}
              item
              sx={{ mt: 2 }}
            >
              <Grid
                container
              >
                <Grid
                  display="contents"
                  item
                  md={12}
                  xs={12}
                >
                  {getGridItem('Channel Partner Name', cpDetailData?.name)}
                  {getGridItem('Primary Email Id', cpDetailData?.primaryEmail)}
                  {getGridItem('Manager Name', cpDetailData?.managementDetails?.name || '--')}
                  {getGridItem('Manager Email Id', cpDetailData?.managementDetails?.email || '--')}
                  {getGridItem('Manager Mobile Number', cpDetailData?.managementDetails?.mobileNumber
                    ? `+91${cpDetailData?.managementDetails?.mobileNumber.toString()}` : '--')}
                </Grid>

              </Grid>
            </Grid>
            <Grid
              item
              md={5}
              sx={{ mt: 2 }}
            >
              <Grid
                container
              >
                <Grid
                  item
                  md={12}
                  xs={12}
                >
                  {getGridItem('Type Of Firm', cpDetailData?.typeOfFirm || '--')}
                  {getGridItem('Mobile Number', `+91${cpDetailData?.primaryMobile?.toString()}`)}
                  {getGridItem('Address', getAddress(cpDetailData?.officeAddress))}
                </Grid>

              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        )}
      </Card>
      {openEditBasicInfo && (
      <EditBasicInfo
        open={openEditBasicInfo}
        onClose={() => setOpenEditBasicInfo(false)}
        channelPartnerDetails={cpDetailData}
        updateBasicDetails={updateBasicDetails}
      />
      )}
    </>
  );
};

BasicInfoCard.propTypes = {
  // @ts-ignore
  cpDetailData: PropTypes.object,
  addProfilePhoto: PropTypes.func,
  updateBasicDetails: PropTypes.func
};
